import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import httpRequest from "../utils/httpRequest";

const Customer = ({ newCustomer, setNewCustomer, locations }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [errors, setErrors] = useState({});

  const handleOpenDialog = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!/^[a-zA-Z\s]*$/.test(value)) return "Only letters and spaces allowed.";
        break;
      case "account_number":
        if (!/^\d*$/.test(value)) return "Account number should be numeric.";
        break;
      case "mobile":
        if (!/^\d{0,10}$/.test(value)) return "Mobile number must be 10 digits.";
        if (value.length !== 10) return "Mobile number must be exactly 10 digits.";
        break;
      case "gst_number":
        if (
          value &&
          !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value)
        )
          return "Invalid GST number format. e.g. 27AAPFU0939F1ZV";
        break;
      case "email":
        if (
          value &&
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
        )
          return "Invalid email address.";
        break;
      default:
        return "";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer((prev) => ({
      ...prev,
      [name]: value,
    }));

    const error = validateField(name, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const validateAllFields = () => {
    const newErrors = {};
    Object.entries(newCustomer).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateAllFields()) return;

    try {
      await httpRequest("customers/create", "POST", newCustomer);
      setSnackbar({
        open: true,
        message: "Customer added successfully!",
        severity: "success",
      });
      handleCloseDialog();
      setNewCustomer({
        name: "",
        gst_number: "",
        account_number: "",
        mobile: "",
        address: "",
        email: "",
        location: "",
      });
      setErrors({});
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || "Failed to add customer",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenDialog}
      >
        Add Customer
      </Button>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={newCustomer.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Account Number"
            name="account_number"
            value={newCustomer.account_number}
            onChange={handleChange}
            error={!!errors.account_number}
            helperText={errors.account_number}
            fullWidth
            margin="normal"
          />
          <TextField
            label="GST Number"
            name="gst_number"
            value={newCustomer.gst_number}
            onChange={handleChange}
            error={!!errors.gst_number}
            helperText={errors.gst_number}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Mobile"
            name="mobile"
            value={newCustomer.mobile}
            onChange={handleChange}
            error={!!errors.mobile}
            helperText={errors.mobile}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Address"
            name="address"
            value={newCustomer.address}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            name="email"
            value={newCustomer.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Location</InputLabel>
            <Select
              name="location"
              value={newCustomer.location || ""}
              onChange={handleChange}
              label="Location"
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.location}>
                  {loc.location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Customer;
